import React from 'react';
import { AdminTrade, AdminTradeItem } from 'store/services/trade/tradeApi';
import { AssetUrls } from 'config/assets';
import { FoilCardImage } from 'components/FoilCardImage';
import { Points } from 'components/Points/Points';
import { getLanguageDisplayName } from 'constants/languages';

const CONDITION_MAP: Record<string, string> = {
  NEAR_MINT: 'NM',
  SLIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
  NM: 'NM',
  LP: 'LP',
  MP: 'MP',
  HP: 'HP',
  DMG: 'DMG',
};

interface Props {
  trade: AdminTrade;
  onEditItem?: (trade: AdminTrade, item: AdminTradeItem) => void;
  onRemoveItem?: (trade: AdminTrade, item: AdminTradeItem) => void;
  onCompleteItem?: (trade: AdminTrade, item: AdminTradeItem) => void;
  completingItemId?: string | null;
  readOnly?: boolean;
  colSpan?: number;
}

export default function AdminTradeExpandedRow({
  trade,
  onEditItem,
  onRemoveItem,
  onCompleteItem,
  completingItemId,
  readOnly = false,
  colSpan = 9,
}: Props) {
  return (
    <tr className="trades-expanded-row">
      <td colSpan={colSpan}>
        <div className="trades-expanded-content">
          <table className="trades-items-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>Art</th>
                <th>Card Name</th>
                <th>Set</th>
                <th>Condition</th>
                <th>Language</th>
                <th>Foil</th>
                <th>Qty</th>
                <th>Value</th>
                {!readOnly && <th style={{ width: 120 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {trade.items.map((item) => (
                <tr key={item.id} className="trades-item-row">
                  <td>
                    {/* Foil art gets the canonical overlay (consistency rule);
                        the Foil COLUMN below stays — it is tabular data, not a
                        tag on the art. */}
                    <FoilCardImage
                      src={AssetUrls.cards.getCardImage(item.scryfallId, 'art_crop')}
                      alt={item.name}
                      foil={!!item.foil}
                      className="trades-item-art-crop"
                      borderRadius="4px"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `${AssetUrls.base}/assets/card-back-default.png`;
                      }}
                    />
                  </td>
                  <td className="trades-item-name">{item.name}</td>
                  <td className="trades-item-set">{(item.setCode || '').toUpperCase()}</td>
                  <td>{CONDITION_MAP[item.condition] || item.condition}</td>
                  <td>{getLanguageDisplayName(item.language || 'en')}</td>
                  <td>
                    {item.foil && <span className="trades-foil-badge">FOIL</span>}
                  </td>
                  <td>{item.quantity}</td>
                  <td><Points points={item.pointValue} type="small" /></td>
                  {!readOnly && (
                    <td>
                      {item.completedAt ? (
                        <span className="trades-item-completed-badge">✓ Completed</span>
                      ) : (
                        <div className="trades-item-actions">
                          <button
                            className="trades-item-action-btn trades-item-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditItem?.(trade, item);
                            }}
                            title="Edit item"
                          >
                            Edit
                          </button>
                          <button
                            className="trades-item-action-btn trades-item-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveItem?.(trade, item);
                            }}
                            title="Remove item"
                          >
                            Remove
                          </button>
                          <button
                            className="trades-item-action-btn trades-item-complete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCompleteItem?.(trade, item);
                            }}
                            disabled={completingItemId === item.id}
                            title="Complete item (credit sender)"
                          >
                            Complete
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}
